import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { tap } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from '@app/shared/services/auth.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { loadWith } from '@dg/shared-rxjs';
import { WindowToken } from '@app/shared/window.token';
import { OrgSettingsService } from '@app/orgs/services/org-settings.service';
import {
  SettingsPermission,
  SettingsRole,
} from '@app/orgs/services/orgs.model';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { OrgInfo } from '@app/account/account-api.model';
import { LDFlagsService } from '@dg/shared-services';

/**
 * @visibleName Org Security
 *
 * This component displays the list of available permissions for an org, and the roles to which
 * those permissions have been assigned.
 *
 * @param orgId The Organization to manage permissions. This may be the user's default Org, or a Channel Org
 * @param availablePermissions The list of permissions that may be assigned to roles in this org
 * @param toggleTitle Controls the display of the title on the sticky header
 * @param isChannel The user is editing permissions for a Channel org, instead of their default Org
 * @param rolesChange Emits when the assigned permissions for roles have changed
 * @param resetForm Resets the assigned permissions to roles when set to `true`
 *
 * @usage
 *
 * <dgx-org-security
 *   [orgId]="channelId"
 *   [toggleTitle]="true"
 *   [availablePermissions]="permissions"
 *   [isChannel]="true"
 *   [resetForm]="resetForm"
 *   (roles-change)="onRolesChange($event)"
 * ></dgx-org-security>
 */
@Component({
  selector: 'dgx-org-security',
  templateUrl: './org-security.component.html',
})
export class OrgSecurityComponent
  extends SubscriberBaseDirective
  implements OnChanges, OnInit
{
  @Input() public orgId: number;
  @Input() public availablePermissions: SettingsPermission[];
  @Input() public toggleTitle: boolean = false;
  @Input() public isChannel: boolean = false;
  @Input() public resetForm: boolean = false;
  @Output() public rolesChange = new EventEmitter<SettingsRole[]>();

  // Translation
  public readonly i18n = this.translate.instant([
    'OrgSecurityCtrl_RolesUpdatedSuccessMessage',
    'OrgSecurityCtrl_CreateGroups',
    'dgOrgSecurity_LearnMore',
    'dgOrgSecurity_DefaultRoleText',
    'dgOrgSecurity_CreateGroups',
    'dgOrgSecurity_CreateOpportunities',
    'Core_Permissions',
    'Core_Save',
  ]);

  public canViewKnowledgeCenter: boolean = false;
  public isLoading = false;
  public roles: SettingsRole[] = [];
  private userOrg: OrgInfo;

  private originalPermissions: SettingsPermission[];

  constructor(
    @Inject(WindowToken) private windowRef: Window,
    private translate: TranslateService,
    private authService: AuthService,
    private orgSettingsService: OrgSettingsService,
    private tracker: TrackerService,
    private cdr: ChangeDetectorRef,
    private ldFlagService: LDFlagsService
  ) {
    super();
  }
  public get showDynamicGroupsV2(): boolean {
    return this.ldFlagService.orgManagement.dynamicGroupsV2;
  }
  public ngOnInit() {
    // NOTE: `can${permission}` properties from the default org are not available on the ngx AuthUser
    this.userOrg = this.authService.authUser.orgInfo.find(
      (o) => o.organizationId === this.orgId
    );
    this.canViewKnowledgeCenter =
      this.userOrg?.permissions?.viewKnowledgeCenter;

    // Store the original copy
    this.originalPermissions = cloneDeep(this.availablePermissions);
    this.loadRoles();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.availablePermissions?.currentValue &&
      changes.availablePermissions?.firstChange === false
    ) {
      // load the latest roles, and merge with the available permissions
      this.loadRoles();
    } else if (changes.resetForm?.currentValue === true) {
      // reset/reload the permissions when the parent component toggles this input
      this.availablePermissions = cloneDeep(this.originalPermissions);
      this.loadRoles();
    }
  }

  public updateRoles(roles: SettingsRole[]) {
    this.rolesChange.emit(roles);
  }

  public clickedLearnMoreLink(link: string): void {
    this.tracker.trackEventData({ action: 'Knowledge Center Link Clicked' });
  }

  public trackRoleById(index: number, r: SettingsRole) {
    return r.roleId;
  }

  public trackPermissionById(index: number, p: SettingsPermission) {
    return p.permissionId ?? p.id;
  }

  /**
   * TODO: no magic numbers - the methods should pull from a source
   */
  public isDisabledPermission(
    permission: SettingsPermission,
    role: SettingsRole
  ) {
    // Disable permissions for reports that are downloadable in the app when the Org Setting is enabled
    if (
      this.userOrg?.settings.downloadableReportPermissionReadOnly &&
      (permission.permissionId === /* DownloadReports */ 23 ||
        permission.permissionId === /* DownloadPathwayDetails */ 31 ||
        permission.permissionId === /* ManageReportingInApp */ 74)
    ) {
      return true;
    }

    // Disable permissions for
    // (74-ManageReportingInApp) In-App Reporting for non admins
    // (83-ManageReportingTransmitterConfigurations) Manage Transmitter Configs for non admins
    let disabledReportingPermissions = [74, 83];
    if (
      disabledReportingPermissions.includes(permission.permissionId) &&
      !role.isAdmin
    ) {
      return true;
    }

    const disableManageCoachPermission = new Set([87]);
    const disabledRoleIds = new Set([6]);
    if (
      disableManageCoachPermission.has(permission.permissionId) &&
      disabledRoleIds.has(role.roleId)
    ) {
      return true;
    }

    // Disable an admin role from editing permissions required for other admins
    if (permission.isRequiredForAdmin && role.isAdmin) return true;

    // Disable a member role from editing permissions NOT for members
    if (!permission.isMemberPermission && role.roleId === 2) return true;

    return false;
  }

  private loadRoles(): void {
    const roles$ = this.orgSettingsService.getRoles(this.orgId).pipe(
      tap((roles: any) => {
        this.onGetRoles(roles);
        this.cdr.markForCheck();
      }),
      this.takeUntilDestroyed()
    );

    loadWith(roles$, this).subscribe();
  }

  private onGetRoles(roles: SettingsRole[]) {
    let addParentCreateGroup = false;
    let addParentCreateOpportunities = false;
    let addParentBulkUpoload = false;
    let addParentReporting = false;

    for (const role of roles) {
      const permissions = cloneDeep(this.availablePermissions);
      const assignedPermissionIds = new Set<number>(
        role.permissions.map((p) => p.permissionId)
      );

      let indexesModified = 0;
      let parentGroupIndex;
      let parentCreateOpportunitiesIndex;
      let parentBulkUploadIndex;
      let parentReportingIndex;
      role.labelKey = 'Core_Role' + role.name.replace(/\s/g, ''); // i18n look up key.

      permissions.forEach((perm, index) => {
        perm.assigned = assignedPermissionIds.has(perm.permissionId);

        if (
          !addParentCreateGroup &&
          this.isCreateGroupPermission(perm.permissionId)
        ) {
          parentGroupIndex = index + indexesModified;
          addParentCreateGroup = true;
          indexesModified++;
        } else if (
          !addParentCreateOpportunities &&
          this.isCreateOpportunityPermission(perm.permissionId)
        ) {
          parentCreateOpportunitiesIndex = index + indexesModified;
          addParentCreateOpportunities = true;
          indexesModified++;
        } else if (
          !addParentBulkUpoload &&
          this.isBulkUploadPermission(perm.permissionId)
        ) {
          parentBulkUploadIndex = index + indexesModified;
          addParentBulkUpoload = true;
          indexesModified++;
        } else if (
          !addParentReporting &&
          this.isReportingPermission(perm.permissionId)
        ) {
          parentReportingIndex = index + indexesModified;
          addParentReporting = true;
          indexesModified++;
        }
        this.availablePermissions[index].nested = this.isNested(perm);
        perm.labelKey = perm.name.replace(/\s/g, ''); // i18n look up key.

        perm.isDisabled = this.isDisabledPermission(perm, role);
      });

      // NOTE: additional entries are added to the permissions list, serving as a header for a
      // nested set of permissions

      // Keep outside the forEach since it manipulates the ordering/index
      if (parentGroupIndex) {
        this.availablePermissions.splice(parentGroupIndex, 0, {
          /* don't use an i18n string here, it gets i18nized further down, relying on this exact "Name" */
          name: 'Create Groups',
          id: 'CreateGroups',
        } as any);
      }

      if (parentCreateOpportunitiesIndex) {
        this.availablePermissions.splice(parentCreateOpportunitiesIndex, 0, {
          /* don't use an i18n string here, it gets i18nized further down, relying on this exact "Name" */
          name: 'Create Opportunities',
          id: 'CreateOpportunities',
        } as any);
      }

      if (parentBulkUploadIndex) {
        this.availablePermissions.splice(parentBulkUploadIndex, 0, {
          /* don't use an i18n string here, it gets i18nized further down, relying on this exact "Name" */
          name: 'Upload Files',
          id: 'UploadFiles',
        } as any);
      }

      if (parentReportingIndex) {
        this.availablePermissions.splice(parentReportingIndex, 0, {
          /* don't use an i18n string here, it gets i18nized further down, relying on this exact "Name" */
          name: 'Manage Reporting',
          id: 'ManageReporting',
        } as any);
      }

      role.permissions = permissions;
    }

    this.roles = roles;

    for (const permission of this.availablePermissions) {
      // i18n look up key
      if (permission.name) {
        const baseString: string =
          'dgOrgSecurity_' + permission.name.replace(/\s/g, '');
        permission.labelKey =
          !this.showDynamicGroupsV2 &&
          (permission.name === 'Create Administrative Groups' ||
            permission.name === 'Manage Segments')
            ? `${baseString}_Old`
            : baseString;
        permission.descriptionKey =
          !this.showDynamicGroupsV2 && permission.name === 'Manage Segments'
            ? baseString + 'Description_Old'
            : baseString + 'Description';
      } else {
        permission.labelKey = permission.descriptionKey = '';
      }
    }
  }

  /**
   * TODO: no magic numbers - the methods should pull from a source
   */
  private isCreateGroupPermission(permissionId: number) {
    return (
      // granular create group permission
      permissionId === 11 ||
      permissionId === 12 ||
      permissionId === 13 ||
      permissionId === 22
    );
  }

  /**
   * TODO: no magic numbers - the methods should pull from a source
   */
  private isCreateOpportunityPermission(permissionId: number) {
    return (
      // create opportunity type permissions
      permissionId === 48 ||
      permissionId === 49 ||
      permissionId === 50 ||
      permissionId === 51 ||
      permissionId === 52 ||
      permissionId === 53 ||
      permissionId === 55 ||
      permissionId === 56 ||
      permissionId === 57 ||
      permissionId === 58 ||
      permissionId === 59 ||
      permissionId === 60
    );
  }

  /**
   * TODO: no magic numbers - the methods should pull from a source
   */
  private isBulkUploadPermission(permissionId: number) {
    return (
      // create opportunity type permissions
      permissionId === 64 ||
      permissionId === 65 ||
      permissionId === 66 ||
      permissionId === 67 ||
      permissionId === 68 ||
      permissionId === 69 ||
      permissionId === 70 ||
      permissionId === 71 ||
      permissionId === 72
    );
  }

  private isReportingPermission(permissionId: number) {
    return (
      permissionId === /* MangeReportingInApp */ 74 ||
      permissionId === /* ManageReportingFTPScheduler */ 76 ||
      permissionId === /* Manage Reporting Transmitter Configurations */ 83
    );
  }

  /**
   * TODO: Nested permissions need to be handled from the server-side.
   * In the meantime, these are the nested permissions by id, hardcoded
   */
  private isNested(item: SettingsPermission) {
    return (
      this.isCreateGroupPermission(item.permissionId) ||
      // download detailed reports permissions
      item.permissionId === 31 ||
      // create opportunity type permissions
      this.isCreateOpportunityPermission(item.permissionId) ||
      // create bulk upload type permissions
      this.isBulkUploadPermission(item.permissionId) ||
      this.isReportingPermission(item.permissionId)
    );
  }
}
